import { ActionIcon, Badge, Popover } from '@mantine/core';
import { IconPointer, IconSettings } from '@tabler/icons-react';
import { useState } from 'react';
import { useUpsertOperation } from '~/hooks/mutations/operations/useUpsertOperation';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type { OperationParentContext, AIActionModel } from '~/server/logic/steps/operation/types';
import AIActionOpConfig from './aiAction/AIActionOpConfig';
import AIOpInPlaceEdit from './AIOpInPlaceEdit';

export default function AIActionOp({
  operation,
  testContentId,
  stepId,
  stepOrder,
  previewCursorAllowed,
  readonly,
  disableMenu,
  parentContext,
}: {
  operation: SelectedOperation;
  testContentId: string;
  stepId: string;
  stepOrder: number;
  previewCursorAllowed: boolean;
  readonly?: boolean;
  disableMenu?: boolean;
  parentContext?: OperationParentContext;
}) {
  const [isGearPopoverOpen, setIsGearPopoverOpen] = useState(false);

  const {
    mutateAsync: upsertOperation,
    isPending: isSaving,
    variables: mutationVariables,
  } = useUpsertOperation({
    testContentId,
    stepId,
    parentContext,
  });

  const handleGearSave = async (values: { prompt: string; model: AIActionModel }) => {
    setIsGearPopoverOpen(false);
    await upsertOperation({
      operation: {
        operationId: operation.id,
        pageAlias: operation.pageAlias,
        opensPageAlias: operation.opensPageAlias || undefined,
        parentContext,
        operation: {
          operationType: 'AI_ACTION',
          prompt: values.prompt,
          model: values.model,
        },
      },
    }).catch(() => undefined);
  };

  const handlePromptUpdate = async (prompt: string) => {
    await upsertOperation({
      operation: {
        operationId: operation.id,
        pageAlias: operation.pageAlias,
        opensPageAlias: operation.opensPageAlias || undefined,
        parentContext,
        operation: {
          operationType: 'AI_ACTION',
          prompt,
          model: operation.aiActionOp?.model || 'STABLY_FLASH',
        },
      },
    }).catch(() => undefined);
  };

  const badge = (
    <Badge
      leftSection={<IconPointer size={14} />}
      color="violet"
      variant="filled"
      className="bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/20 dark:text-purple-400"
      classNames={{ root: 'normal-case' }}
    >
      Act
    </Badge>
  );

  const localPrompt =
    isSaving && mutationVariables?.operation.operation.operationType === 'AI_ACTION' ?
      mutationVariables.operation.operation.prompt
    : undefined;
  const remotePrompt = operation.aiActionOp?.prompt;

  const gearIconContent = (
    <Popover
      opened={isGearPopoverOpen}
      onClose={() => setIsGearPopoverOpen(false)}
      onDismiss={() => setIsGearPopoverOpen(false)}
      position="bottom"
      width={360}
      withArrow
      shadow="md"
      closeOnClickOutside
    >
      <Popover.Target>
        <ActionIcon
          size="xs"
          radius="xl"
          variant="transparent"
          color="gray"
          className="hover:bg-gray-100 hover:dark:bg-gray-800"
          onClick={() => {
            setIsGearPopoverOpen((prev) => !prev);
          }}
        >
          <IconSettings />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown className="dark:bg-neutral-800">
        <AIActionOpConfig
          operation={operation}
          onClose={() => setIsGearPopoverOpen(false)}
          onSave={handleGearSave}
          readonly={readonly}
          optimisticPrompt={localPrompt}
        />
      </Popover.Dropdown>
    </Popover>
  );

  return (
    <AIOpInPlaceEdit
      operation={operation}
      testContentId={testContentId}
      stepOrder={stepOrder}
      previewCursorAllowed={previewCursorAllowed}
      readonly={readonly}
      disableMenu={disableMenu}
      parentContext={parentContext}
      badge={badge}
      gearIconContent={gearIconContent}
      prompt={(localPrompt ?? remotePrompt ?? '').trim()}
      placeholderText="Click to enter prompt"
      onPromptUpdate={handlePromptUpdate}
    />
  );
}
